import { inject, observer } from 'mobx-react';
import classNames from 'classnames';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Modal } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import CountryStore from '../../../store/CountryStore';
import CurrencyStore from '../../../store/CurrencyStore';
import LanguageStore from '../../../store/LanguageStore';
import classifyRegionalSettingsPresentation, {
  REGIONAL_LAYOUT,
} from '../../../util/classifyRegionalSettingsPresentation';
import { countryCodeFromLocale } from '../../../util/languageMapper';
import LocaleFlagIcon from '../../common/LocaleFlagIcon';
import RegionalSettings from '../../common/RegionalSettings';
import RegionalSettingsCurrencyDropdown from '../../common/RegionalSettingsCurrencyDropdown';
import RegionalSettingsDeliveryCountryDropdown from '../../common/RegionalSettingsDeliveryCountryDropdown';
import RegionalSettingsPanelContent from '../../common/RegionalSettingsPanelContent';
import RegionalSettingsInlineLanguageSelector from '../../common/RegionalSettingsInlineLanguageSelector';
import RegionalSettingsLanguageDropdown from '../../common/RegionalSettingsLanguageDropdown';
import ErrorHandler from '../../loader/ErrorHandler';
import { createInlineErrorModel } from '../../../util/error';
import HeaderNavAction from '../HeaderNavAction';

const DIALOG_ID = 'navigation-regional-settings-dialog';
const DIALOG_TITLE_ID = 'navigation-regional-settings-dialog-title';

const createCombinedSeparator = (key) => (
  <span
    key={key}
    className="NavigationRegionalSettingsControls__trigger-separator"
  >
    /
  </span>
);

const NavigationRegionalSettingsControls = ({
  languageStore,
  currencyStore,
  countryStore,
}) => {
  const [open, setOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const dialogBodyRef = useRef(null);
  const triggerRef = useRef(null);
  const shouldRestoreFocusRef = useRef(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const presentation = classifyRegionalSettingsPresentation({
    languageCount: languageStore.languages.length,
    countryCount: countryStore.countries.length,
    currencyCount: currencyStore.currencies.length,
  });
  const {
    layoutMode,
    showLanguages,
    showCountries,
    showCurrencies,
    singleSelectorType,
    pairedSelectorType,
  } = presentation;

  const isInlineLanguage =
    layoutMode === REGIONAL_LAYOUT.INLINE_LANGUAGE &&
    singleSelectorType === 'language';
  const isInlineLanguageWithDropdown =
    layoutMode === REGIONAL_LAYOUT.INLINE_LANGUAGE_WITH_DROPDOWN;
  const showsInlineLanguageRow =
    isInlineLanguage || isInlineLanguageWithDropdown;
  const isSingleLanguageDropdown =
    layoutMode === REGIONAL_LAYOUT.SINGLE_DROPDOWN &&
    singleSelectorType === 'language';
  const isSingleCountryDropdown =
    layoutMode === REGIONAL_LAYOUT.SINGLE_DROPDOWN &&
    singleSelectorType === 'country';
  const isSingleCurrencyDropdown =
    layoutMode === REGIONAL_LAYOUT.SINGLE_DROPDOWN &&
    singleSelectorType === 'currency';
  const isSingleDropdown =
    isSingleLanguageDropdown ||
    isSingleCountryDropdown ||
    isSingleCurrencyDropdown;
  const usesCombinedTrigger = layoutMode === REGIONAL_LAYOUT.COMBINED_TRIGGER;

  const labels = [];
  if (showLanguages) {
    if (showCurrencies) {
      labels.push(
        <span
          key="currency"
          className="NavigationRegionalSettingsControls__trigger-currency"
        >
          {currencyStore.currencyCode}
        </span>
      );
    }
    if (showCountries) {
      labels.push(
        <span
          key="country"
          className="NavigationRegionalSettingsControls__trigger-country"
        >
          <span className="NavigationRegionalSettingsControls__trigger-country-code">
            {countryStore.activeCountry.iso_code_2.toUpperCase()}
          </span>
        </span>
      );
    }
  } else {
    // Country ± currency without language: [Flag] [code] / EUR
    if (showCountries) {
      const activeCountry = countryStore.activeCountry;
      labels.push(
        <span
          key="country"
          className="NavigationRegionalSettingsControls__trigger-country"
        >
          <LocaleFlagIcon
            locale={activeCountry.iso_code_2.toLowerCase()}
            decorative
          />
          <span className="NavigationRegionalSettingsControls__trigger-country-code">
            {activeCountry.iso_code_2.toUpperCase()}
          </span>
        </span>
      );
    }
    if (showCurrencies) {
      labels.push(
        <span
          key="currency"
          className="NavigationRegionalSettingsControls__trigger-currency"
        >
          {currencyStore.currencyCode}
        </span>
      );
    }
  }

  if (!showLanguages && !showCountries && !showCurrencies) {
    return null;
  }

  const labelIcon = showLanguages ? (
    <span className="NavigationRegionalSettingsControls__trigger-language">
      <LocaleFlagIcon
        locale={countryCodeFromLocale(languageStore.activeLanguage.code)}
        decorative
      />
    </span>
  ) : null;

  const restoreFocusToTrigger = () => {
    if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
      triggerRef.current.focus();
    }
  };

  const closeSurface = ({ restoreFocus = true } = {}) => {
    shouldRestoreFocusRef.current = restoreFocus;
    setOpen(false);
  };

  const handleDialogClosed = () => {
    if (!shouldRestoreFocusRef.current) {
      return;
    }
    shouldRestoreFocusRef.current = false;
    // Defer past the closing click so backdrop dismiss does not leave focus on body.
    window.setTimeout(() => {
      restoreFocusToTrigger();
    }, 0);
  };

  const handleDialogOpened = () => {
    if (
      dialogBodyRef.current &&
      typeof dialogBodyRef.current.focus === 'function'
    ) {
      dialogBodyRef.current.focus();
    }
  };

  const renderPairedDropdown = (commitSelection, currentValues, isSaving) => {
    if (pairedSelectorType === 'country' || isSingleCountryDropdown) {
      return (
        <RegionalSettingsDeliveryCountryDropdown
          country={currentValues.country}
          countries={countryStore.countries}
          onSelect={(country) => commitSelection('country', country)}
          showLabelText
          toggleClassName="nav-link"
          toggleNav
          disabled={isSaving}
        />
      );
    }

    if (pairedSelectorType === 'currency' || isSingleCurrencyDropdown) {
      return (
        <RegionalSettingsCurrencyDropdown
          currency={currentValues.currency}
          currencies={currencyStore.currencies}
          onSelect={(currency) => commitSelection('currency', currency)}
          showLabelText
          toggleClassName="nav-link"
          toggleNav
          disabled={isSaving}
        />
      );
    }

    return null;
  };

  return (
    <RegionalSettings>
      {(
        save,
        onChange,
        currentValues,
        isSaving,
        errorState,
        commitSelection,
        resetSelectionState
      ) => {
        const dismissModal = () => {
          if (isSaving) {
            return;
          }
          resetSelectionState();
          closeSurface();
        };

        const handleDialogKeyDown = (event) => {
          if (event.key === 'Escape') {
            event.stopPropagation();
            dismissModal();
          }
        };

        return (
          <Fragment>
            <li
              className={classNames(
                'NavigationRegionalSettingsControls',
                `NavigationRegionalSettingsControls--${layoutMode}`,
                {
                  'NavigationRegionalSettingsControls--inline-language-row':
                    showsInlineLanguageRow,
                },
                'nav-item'
              )}
            >
              {isInlineLanguageWithDropdown ? (
                <div className="NavigationRegionalSettingsControls__inline-language-with-dropdown">
                  <RegionalSettingsInlineLanguageSelector
                    language={currentValues.language}
                    languages={languageStore.languages}
                    onSelect={(language) =>
                      commitSelection('language', language)
                    }
                    disabled={isSaving}
                  />
                  <div className="NavigationRegionalSettingsControls__header-dropdown">
                    {renderPairedDropdown(
                      commitSelection,
                      currentValues,
                      isSaving
                    )}
                  </div>
                </div>
              ) : isInlineLanguage ? (
                <RegionalSettingsInlineLanguageSelector
                  language={currentValues.language}
                  languages={languageStore.languages}
                  onSelect={(language) => commitSelection('language', language)}
                  disabled={isSaving}
                />
              ) : isSingleDropdown ? (
                <div className="NavigationRegionalSettingsControls__header-dropdown">
                  {isSingleLanguageDropdown && (
                    <RegionalSettingsLanguageDropdown
                      language={currentValues.language}
                      languages={languageStore.languages}
                      onSelect={(language) =>
                        commitSelection('language', language)
                      }
                      showLabelText={false}
                      showCompactFlagTrigger
                      toggleClassName="nav-link"
                      toggleNav
                      disabled={isSaving}
                    />
                  )}
                  {renderPairedDropdown(
                    commitSelection,
                    currentValues,
                    isSaving
                  )}
                </div>
              ) : (
                <HeaderNavAction
                  ref={triggerRef}
                  surface="top"
                  className="NavigationRegionalSettingsControls__trigger dropdown-toggle"
                  onClick={() => setOpen(true)}
                  aria-haspopup="dialog"
                  aria-expanded={open}
                  aria-controls={DIALOG_ID}
                >
                  <span className="sr-only">
                    <FormattedMessage
                      {...globalTranslations.regionalSettingsTitle}
                    />
                  </span>
                  <span className="NavigationRegionalSettingsControls__trigger-label">
                    {labelIcon}
                    {showLanguages ? (
                      <span className="sr-only">
                        {languageStore.activeLanguage.name}
                      </span>
                    ) : null}
                    {labelIcon && labels.length > 0
                      ? createCombinedSeparator('combined-sep-language')
                      : null}
                    {labels.reduce((parts, label, index) => {
                      if (index > 0) {
                        parts.push(
                          createCombinedSeparator(`combined-sep-${index}`)
                        );
                      }
                      parts.push(label);
                      return parts;
                    }, [])}
                  </span>
                </HeaderNavAction>
              )}
            </li>
            {!usesCombinedTrigger && errorState.hasError && (
              <li className="NavigationRegionalSettingsControls__error nav-item">
                <ErrorHandler
                  error={createInlineErrorModel(errorState.error)}
                />
              </li>
            )}
            {usesCombinedTrigger && isClient && (
              <Modal
                id={DIALOG_ID}
                isOpen={open}
                toggle={dismissModal}
                className="NavigationRegionalSettingsControls__dialog"
                labelledBy={DIALOG_TITLE_ID}
                onOpened={handleDialogOpened}
                onClosed={handleDialogClosed}
                fade={false}
              >
                <RegionalSettingsPanelContent
                  currentValues={currentValues}
                  onChange={onChange}
                  errorState={errorState}
                  isSaving={isSaving}
                  onSave={() => {
                    save().then((success) => {
                      if (success) {
                        closeSurface();
                      }
                    });
                  }}
                  onClose={dismissModal}
                  onCancel={dismissModal}
                  titleId={DIALOG_TITLE_ID}
                  contentRef={dialogBodyRef}
                  contentTabIndex={-1}
                  onContentKeyDown={handleDialogKeyDown}
                />
              </Modal>
            )}
          </Fragment>
        );
      }}
    </RegionalSettings>
  );
};

NavigationRegionalSettingsControls.propTypes = {
  languageStore: modelOf(LanguageStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
};

export default inject(
  'languageStore',
  'currencyStore',
  'countryStore'
)(observer(NavigationRegionalSettingsControls));
